import _ from "lodash";
import securityManager from "../../../services/managers/security";
import envManager from "../../../services/managers/env";
import webStorageManager from "../../../services/managers/web-storage";
import pagePreloaderManager from "../../../services/managers/page-preloader";
import alertMessageManager from "../../../services/managers/alert-message";
import chartDataTransformer from "../../../services/transformers/chart-data";
import chartMetaDataTransformer from "../../../services/transformers/chart-meta-data";
import {
  CHART_DRAWING_INITIAL_STATE,
  GRANULARITIES,
  GRANULARITY_DAY,
  ZONES,
  API_KEYS_CTT,
  SELECT_EMPTY_VALUE,
  CHART_CTT,
  CHART_BID_ASK_QUANTITY,
  CHART_TYPE_AREA_MIRRORED
} from "../../../services/managers/constants";
import { MSG_ERR_VIEW_CHART_VALIDATION, MSG_ERR_VIEW_CHART_NO_DATA } from "../../../services/managers/string-literals";
import chartMetaData from "../../../services/transformers/chart-meta-data";

export const onSelectedPrimaryChartChange = (state, action) => {
  let chartWhiteboards = state.chartWhiteboards.slice();
  let whiteboard = { ...chartWhiteboards[action.payload.whiteboardIndex] };

  whiteboard.selectedPrimaryChart = action.payload.value;

  // Reset Primary Chart Types
  whiteboard.primaryChartTypes = chartMetaDataTransformer.getApplicableChartTypesByChart(action.payload.value);
  whiteboard.selectedPrimaryChartType = whiteboard.primaryChartTypes[0]["Code"];

  // Reset Comparison Chart
  whiteboard.selectedComparisonChart = SELECT_EMPTY_VALUE;
  whiteboard.comparisonCharts = chartMetaDataTransformer.getApplicableComparisonChartsByPrimaryChart(whiteboard.selectedPrimaryChart);

  // Reset Comparison Chart Types
  whiteboard.comparisonChartTypes = [];
  whiteboard.selectedComparisonChartType = undefined;

  // Reset Chart Layers
  whiteboard.selectedLayers = chartMetaDataTransformer.getApplicableChartLayers(
    whiteboard.selectedPrimaryChart,
    whiteboard.selectedPrimaryChartType,
    whiteboard.selectedComparisonChart,
    whiteboard.selectedComparisonChartType
  ).selectiveLayers;

  // Reset Lot Sizes
  whiteboard.selectedCostToTradeLotSizes = [];
  whiteboard.selectedCostToTradeLotSizes[0] = state.costToTradeLotSizes[0].Key;

  // Obsoleting Whiteboard
  whiteboard.isObsolete = true;
  whiteboard.__change_detection_key = securityManager.generateKey();
  chartWhiteboards[action.payload.whiteboardIndex] = whiteboard;

  return { ...state, chartWhiteboards };
};

export const onSelectedComparisonChartChange = (state, action) => {
  let chartWhiteboards = state.chartWhiteboards.slice();
  let whiteboard = { ...chartWhiteboards[action.payload.whiteboardIndex] };

  whiteboard.selectedComparisonChart = action.payload.value;

  // Reset Comparison Chart Types
  whiteboard.comparisonChartTypes = [];
  whiteboard.selectedComparisonChartType = undefined;
  if (whiteboard.selectedComparisonChart !== SELECT_EMPTY_VALUE) {
    whiteboard.comparisonChartTypes = chartMetaDataTransformer.getApplicableChartTypesByChart(action.payload.value);
    whiteboard.selectedComparisonChartType = whiteboard.comparisonChartTypes[0]["Code"];
  }

  // Reset Chart Layers
  whiteboard.selectedLayers = chartMetaDataTransformer.getApplicableChartLayers(
    whiteboard.selectedPrimaryChart,
    whiteboard.selectedPrimaryChartType,
    whiteboard.selectedComparisonChart,
    whiteboard.selectedComparisonChartType
  ).selectiveLayers;

  // Reset Lot Sizes
  if (whiteboard.selectedPrimaryChart !== CHART_CTT) {
    whiteboard.selectedCostToTradeLotSizes = [];
    whiteboard.selectedCostToTradeLotSizes[0] = state.costToTradeLotSizes[0].Key;
  }

  // Select only one lot size when overlay comparison is active
  if (
    whiteboard.selectedPrimaryChart === CHART_CTT &&
    whiteboard.selectedComparisonChart &&
    whiteboard.selectedComparisonChart !== SELECT_EMPTY_VALUE &&
    !envManager.areMultipleLotSizesAllowedForOverlayComparison()
  ) {
    whiteboard.selectedCostToTradeLotSizes = whiteboard.selectedCostToTradeLotSizes.slice(0, 1);
  }

  // Obsoleting Whiteboard
  whiteboard.isObsolete = true;
  whiteboard.__change_detection_key = securityManager.generateKey();
  chartWhiteboards[action.payload.whiteboardIndex] = whiteboard;

  return { ...state, chartWhiteboards };
};

export const onSelectedChartLayerChange = (state, action) => {
  let chartWhiteboards = state.chartWhiteboards.slice();
  let whiteboard = { ...chartWhiteboards[action.payload.whiteboardIndex] };
  let selectedLayers = whiteboard.selectedLayers.slice();

  if (_.includes(selectedLayers, action.payload.value)) {
    selectedLayers = selectedLayers.length > 1 ? _.pull(selectedLayers, action.payload.value) : selectedLayers;
  } else {
    selectedLayers.push(action.payload.value);
    // Remove previously selected value from the dropdown
    selectedLayers = action.payload.whichControl === "Dropdown" ? _.pull(selectedLayers, action.payload.oldValue) : selectedLayers;
  }

  // Check if old and new Layer arrays are identical
  if (!(!_.difference(selectedLayers, whiteboard.selectedLayers).length && !_.difference(whiteboard.selectedLayers, selectedLayers).length)) {
    // Regenerate the Drawings based on selected Layers
    if (whiteboard.chartDrawings.length && !whiteboard.isObsolete) {
      let newDrawings = [];
      _.forEach(whiteboard.chartDrawings, (drawing) => {
        newDrawings.push({ ...drawing, __change_detection_key: securityManager.generateKey() });
      });
      whiteboard.chartDrawings = newDrawings;
    }

    whiteboard.selectedLayers = selectedLayers;
    whiteboard.__change_detection_key = securityManager.generateKey();
    chartWhiteboards[action.payload.whiteboardIndex] = whiteboard;

    return { ...state, chartWhiteboards };
  }

  return { ...state };
};

export const onViewChartClick = (state, action) => {
  // Validation unsuccessful
  if (action.payload.validationFailure) {
    alertMessageManager.show("whiteboardAlertError" + action.payload.whiteboardIndex, MSG_ERR_VIEW_CHART_VALIDATION);
    pagePreloaderManager.hideChartLoader(action.payload.whiteboardIndex);
    return { ...state };
  }

  // Error in API call
  if (action.payload.error) {
    alertMessageManager.show("whiteboardAlertError" + action.payload.whiteboardIndex, action.payload.error);
    pagePreloaderManager.hideChartLoader(action.payload.whiteboardIndex);
    return { ...state };
  }

  // Empty data sent by server
  if (!_.isArray(action.payload.response) || !action.payload.response.length) {
    alertMessageManager.show("whiteboardAlertError" + action.payload.whiteboardIndex, MSG_ERR_VIEW_CHART_NO_DATA);
    pagePreloaderManager.hideChartLoader(action.payload.whiteboardIndex);
    return { ...state };
  }

  // All good, continue aggregation and drawing

  let chartWhiteboards = state.chartWhiteboards.slice();
  let whiteboard = { ...chartWhiteboards[action.payload.whiteboardIndex] };
  let previousChartDrawingLength = whiteboard.chartDrawings.length;
  let isOverlayComparisonActive = whiteboard.selectedComparisonChart && whiteboard.selectedComparisonChart !== SELECT_EMPTY_VALUE ? true : false;

  // Do aggregation

  let aggregatedData = action.payload.response;

  // Selected granularity can arrive from two sources
  // First: When View Chart is clicked, the source is 'redux store'
  // Second: When granularity is clicked, source is 'payload'
  let computedSelectedGranularity = action.payload.shouldChartBeRedrawnWithLocallyAvailableDataOnGranularityChange
    ? action.payload.code
    : whiteboard.selectedGranularity;

  let dayGranularityIndex = _.findIndex(GRANULARITIES, { Code: GRANULARITY_DAY });
  let selectedGranularityIndex = _.findIndex(GRANULARITIES, { Code: computedSelectedGranularity });

  // Do aggregation for Granularity 'Day' and above only
  if (selectedGranularityIndex >= dayGranularityIndex) {
    // Calculate Offset from selected Zone required for aggregation
    let zoneOffset = _.find(ZONES, { Code: state.selectedZone })["Offset"];
    // Calculate properties required for aggregation
    let aggregationProperties = chartDataTransformer.getAggregationProperties(
      whiteboard.selectedPrimaryChart,
      whiteboard.selectedComparisonChart,
      whiteboard.selectedCostToTradeLotSizes
    );

    // Flag to check whether negating of values is required for Bid Ask Quantity Area Mirrored chart
    let isNegatingRequiredForBidAskQuantityAreaMirroredChart =
      (whiteboard.selectedPrimaryChart === CHART_BID_ASK_QUANTITY && whiteboard.selectedPrimaryChartType === CHART_TYPE_AREA_MIRRORED) ||
      (whiteboard.selectedComparisonChart === CHART_BID_ASK_QUANTITY && whiteboard.selectedComparisonChartType === CHART_TYPE_AREA_MIRRORED)
        ? true
        : false;

    // Set updated value for isExcludeHolidays flag
    whiteboard.isExcludeHolidays =  action.payload.isExcludeHolidays !== undefined ?  action.payload.isExcludeHolidays : whiteboard.isExcludeHolidays;

    // Do aggregation
    aggregatedData = chartDataTransformer.aggregate({
      rawData: action.payload.response,
      offset: zoneOffset,
      granularity: computedSelectedGranularity,
      aggregationProperties,
      isNegatingRequiredForBidAskQuantityAreaMirroredChart,
      whiteboard
    });
  }

  let aggregatedDataLen = aggregatedData.length;

  // Reset Drawings for fresh rendering of new charts
  whiteboard.chartDrawings = [];

  // Do stuff for Cost To Trade chart
  if (whiteboard.selectedPrimaryChart === CHART_CTT || whiteboard.selectedComparisonChart === CHART_CTT) {
    // Placeholder to hold segregated data as per Lot Sizes
    let segregatedDataPerLotSize = {};

    let selectedCostToTradeLotSizesLen = whiteboard.selectedCostToTradeLotSizes.length;

    let aggregationPropertiesForChartOtherThanCTT = [];
    if (isOverlayComparisonActive) {
      aggregationPropertiesForChartOtherThanCTT = chartDataTransformer.getAggregationProperties(
        whiteboard.selectedPrimaryChart === CHART_CTT ? whiteboard.selectedComparisonChart : whiteboard.selectedPrimaryChart,
        undefined
      );
    }

    // Create Drawing components as per number of selected Lot Sizes
    for (let lotSizeIndex = 0; lotSizeIndex < selectedCostToTradeLotSizesLen; lotSizeIndex++) {
      let lotSizeKey = whiteboard.selectedCostToTradeLotSizes[lotSizeIndex];
      let __id = securityManager.generateKey() + "__" + lotSizeKey;
      let __change_detection_key = securityManager.generateKey();
      whiteboard.chartDrawings.push({
        ...CHART_DRAWING_INITIAL_STATE,
        __id,
        __change_detection_key,
        // To identify which Drawing is for which Lot Size
        lotSizeKey,
        lotSizeVal: _.find(state.costToTradeLotSizes, { Key: lotSizeKey })["Val"],
        selectedZone: state.selectedZone
      });
      // Create placeholder array to store segregated data as per Lot Sizes
      segregatedDataPerLotSize[lotSizeKey] = [];
    }
    // Traverse Aggregated Data to construct multiple Segregated Data arrays as per Lot Sizes
    for (let dataIndex = 0; dataIndex < aggregatedDataLen; dataIndex++) {
      for (let lotSizeIndex = 0; lotSizeIndex < whiteboard.selectedCostToTradeLotSizes.length; lotSizeIndex++) {
        let lotSizeKey = whiteboard.selectedCostToTradeLotSizes[lotSizeIndex];

        let costToTradeDataObject = {
          [API_KEYS_CTT.timestamp]: aggregatedData[dataIndex][API_KEYS_CTT.timestamp],
          [API_KEYS_CTT.midPrice]: aggregatedData[dataIndex][API_KEYS_CTT.midPrice],
          [API_KEYS_CTT.contract]: aggregatedData[dataIndex][API_KEYS_CTT.contract],
          [API_KEYS_CTT.bid]: aggregatedData[dataIndex][lotSizeKey + API_KEYS_CTT.bid],
          [API_KEYS_CTT.bidDepth]: aggregatedData[dataIndex][lotSizeKey + API_KEYS_CTT.bidDepth],
          [API_KEYS_CTT.ask]: aggregatedData[dataIndex][lotSizeKey + API_KEYS_CTT.ask],
          [API_KEYS_CTT.askDepth]: aggregatedData[dataIndex][lotSizeKey + API_KEYS_CTT.askDepth]
        };

        if (!isOverlayComparisonActive) {
          segregatedDataPerLotSize[lotSizeKey].push(costToTradeDataObject);
        } else {
          segregatedDataPerLotSize[lotSizeKey].push(
            Object.assign({}, costToTradeDataObject, _.pick(aggregatedData[dataIndex], aggregationPropertiesForChartOtherThanCTT))
          );
        }
      }
    }

    // Store the Segregated Data arrays in memory as per Drawing component identifiers
    for (let lotSizeIndex = 0; lotSizeIndex < selectedCostToTradeLotSizesLen; lotSizeIndex++) {
      webStorageManager.set(
        _.find(whiteboard.chartDrawings, { lotSizeKey: whiteboard.selectedCostToTradeLotSizes[lotSizeIndex] })["__id"],
        segregatedDataPerLotSize[whiteboard.selectedCostToTradeLotSizes[lotSizeIndex]]
      );
    }
  } else {
    // Else just initialize one Drawing
    let __id = securityManager.generateKey();
    let __change_detection_key = securityManager.generateKey();
    whiteboard.chartDrawings.push({ ...CHART_DRAWING_INITIAL_STATE, __id, __change_detection_key, selectedZone: state.selectedZone });
    webStorageManager.set(__id, aggregatedData);
  }

  // Enable/diable granularities on first use
  if (!previousChartDrawingLength) {
    whiteboard.granularities = chartMetaData.getEnabledDisabledGranularities(
      state.selectedDateRangeFrom,
      state.selectedDateRangeTo,
      state.selectedZone
    );
    whiteboard.selectedGranularity = chartMetaData.getNewSelectedGranularityBasedOnEnabledDisabledState(
      whiteboard.granularities,
      whiteboard.selectedGranularity
    );
  }

  whiteboard.isObsolete = false;

  if (action.payload.shouldChartBeRedrawnWithLocallyAvailableDataOnGranularityChange) whiteboard.selectedGranularity = action.payload.code;

  pagePreloaderManager.hideChartLoader(action.payload.whiteboardIndex);
  pagePreloaderManager.hide();

  whiteboard.__change_detection_key = securityManager.generateKey();
  chartWhiteboards[action.payload.whiteboardIndex] = whiteboard;

  return { ...state, chartWhiteboards };
};



// WEBPACK FOOTER //
// ./src/store/analytics/chart-header/action-handlers.js